"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VideoFrame = exports.CameraRequestState = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const sdk_core_1 = require("@streem/sdk-core");
const ui_react_1 = require("@streem/ui-react");
const react_1 = require("react");
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const __1 = require("../../..");
const flip_camera_button_1 = require("./camera_flip/flip_camera_button");
const screenshare_button_1 = require("./screenshare/screenshare_button");
const user_audio_status_1 = require("./user_audio_status");
const loading_screen_1 = require("./loading_screen");
const camera_inactive_screen_1 = require("./camera_inactive_screen");
const use_multiplexer_1 = require("../../../hooks/use_multiplexer");
const room_session_1 = require("../../../context/room_session");
const use_determine_ui_mode_1 = require("../../../hooks/use_determine_ui_mode");
const use_canvas_orientation_1 = require("../../../hooks/use_canvas_orientation");
const get_aspect_ratio_constraint_dims_1 = require("../../../util/get_aspect_ratio_constraint_dims");
const company_settings_1 = require("../../../context/company_settings");
/** how long we wait for a camera subject request before timing it out if there is no response from mobile */
const CAM_SUBJECT_REQUEST_TIMEOUT = 1000 * 15;
var CameraRequestState;
(function (CameraRequestState) {
    CameraRequestState[CameraRequestState["INITIAL"] = 0] = "INITIAL";
    CameraRequestState[CameraRequestState["PENDING"] = 1] = "PENDING";
    CameraRequestState[CameraRequestState["REQUEST_DENIED"] = 2] = "REQUEST_DENIED";
    CameraRequestState[CameraRequestState["REQUEST_TIMEOUT"] = 3] = "REQUEST_TIMEOUT";
})(CameraRequestState = exports.CameraRequestState || (exports.CameraRequestState = {}));
exports.VideoFrame = (0, react_1.forwardRef)(({ cardOptions = {
    constrainContainerToCanvas: false,
    localPipView: false,
}, animationOptions = {
    duration: 0,
    delay: 0,
}, videoController, showArrow, flipArrow, }, ref) => {
    var _a, _b, _c;
    const { styles, constrainContainerToCanvas, localPipView } = cardOptions;
    const { animating, duration: animationDuration, delay: animationDelay } = animationOptions;
    const theme = (0, ui_react_1.useTheme)();
    const roomSession = (0, room_session_1.useRoomSession)();
    const { faceToFaceModeEnabled, screenshareEnabled } = (0, company_settings_1.useCompanySettings)();
    const [cameraRequestState, setCameraRequestState] = (0, react_1.useState)(CameraRequestState.INITIAL);
    const [cameraSubject] = (0, __1.useObservable)(videoController.cameraSubject);
    const [responses] = (0, __1.useObservable)(roomSession.camera.responses);
    const responsesRef = (0, react_1.useRef)(responses);
    (0, react_1.useEffect)(() => {
        responsesRef.current = responses;
    }, [responses]);
    const [latestRemoteCameraSubjectThatIsNotScreenshare] = (0, __1.useObservable)(roomSession.camera.latestRemoteCameraSubjectThatIsNotScreenshare);
    const [cameraSubjectChangeRequestId, setCameraSubjectChangeRequestId] = (0, react_1.useState)(null);
    const [latestCameraSubjectChangeResponse] = (0, __1.useObservable)(roomSession.camera.latestResponse);
    const [latestCameraSubjectChangeRequest] = (0, __1.useObservable)(roomSession.camera.latestRequest);
    // Feed what we know about the current display layout into useDetermineUIMode, and it'll tell us what display mode to use.
    const determinedUIProperties = (0, use_determine_ui_mode_1.useDetermineUIMode)({
        videoController,
    });
    const clientCapabilities = (0, __1.useObservable)(roomSession.room.remoteParticipantCapabilities);
    (0, react_1.useEffect)(() => {
        // ensure the user is prompted before the tab closes
        const handleBeforeUnload = (e) => {
            // Chrome requires this value to be set
            e.returnValue = true;
            // returning a string is the only way to ensure the prompt happens on all other browsers
            return '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        // make sure we remove this listener so the user is no longer prompted once a call is complete
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    });
    (0, react_1.useEffect)(() => {
        if (cameraSubjectChangeRequestId ===
            (latestCameraSubjectChangeResponse === null || latestCameraSubjectChangeResponse === void 0 ? void 0 : latestCameraSubjectChangeResponse.inResponseToWallItemId)) {
            if (
            // legacy versions of mobile apps may not set status, so check accepted too
            (latestCameraSubjectChangeResponse === null || latestCameraSubjectChangeResponse === void 0 ? void 0 : latestCameraSubjectChangeResponse.accepted) ||
                (latestCameraSubjectChangeResponse === null || latestCameraSubjectChangeResponse === void 0 ? void 0 : latestCameraSubjectChangeResponse.status) ===
                    streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ACCEPTED) {
                setCameraRequestState(CameraRequestState.INITIAL);
            }
            else if ((latestCameraSubjectChangeResponse === null || latestCameraSubjectChangeResponse === void 0 ? void 0 : latestCameraSubjectChangeResponse.status) ===
                streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ABORTED) {
                setCameraRequestState(CameraRequestState.REQUEST_TIMEOUT);
            }
            else if (
            // legacy versions of mobile apps may not set status, so check accepted==false
            !(latestCameraSubjectChangeResponse === null || latestCameraSubjectChangeResponse === void 0 ? void 0 : latestCameraSubjectChangeResponse.accepted) ||
                (latestCameraSubjectChangeResponse === null || latestCameraSubjectChangeResponse === void 0 ? void 0 : latestCameraSubjectChangeResponse.status) ===
                    streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_REJECTED) {
                setCameraRequestState(CameraRequestState.REQUEST_DENIED);
            }
            else {
                // Print Error Modal on unexpected response
                setCameraRequestState(CameraRequestState.REQUEST_TIMEOUT);
            }
        }
    }, [latestCameraSubjectChangeResponse, cameraSubjectChangeRequestId]);
    const [freezeFrameImgSrc, setFreezeFrameImgSrc] = (0, react_1.useState)(undefined);
    (0, react_1.useEffect)(() => {
        const enabledSubscription = videoController.enabled.subscribe(enabled => {
            if (!enabled && localPipView && canvasContainer && canvasContainer.current) {
                const remoteCanvas = canvasContainer.current.querySelector('canvas');
                if (remoteCanvas) {
                    // take a snapshot of the remote video canvas
                    // use as a freeze frame while video feed is turning off
                    const canvasDataURL = remoteCanvas.toDataURL('image/jpeg', 0.4);
                    setFreezeFrameImgSrc(canvasDataURL);
                }
            }
            else {
                setFreezeFrameImgSrc(undefined);
            }
        });
        return () => {
            enabledSubscription.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoController]);
    (0, react_1.useEffect)(() => {
        if (((latestCameraSubjectChangeResponse === null || latestCameraSubjectChangeResponse === void 0 ? void 0 : latestCameraSubjectChangeResponse.accepted) ||
            streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ACCEPTED) &&
            canvasContainer &&
            canvasContainer.current &&
            !videoController.ownerIsLocalUser) {
            const remoteCanvas = canvasContainer.current.querySelector('canvas');
            if (remoteCanvas) {
                // take a snapshot of the remote video canvas
                // use as a freeze frame while video feed is switching and animating
                const canvasDataURL = remoteCanvas.toDataURL('image/jpeg', 0.4);
                setFreezeFrameImgSrc(canvasDataURL);
                // catch all remove frozen frame
                setTimeout(() => {
                    setFreezeFrameImgSrc(undefined);
                }, (animationDelay || 0) + animationDuration * 2);
            }
        }
        return () => {
            if (freezeFrameImgSrc) {
                setFreezeFrameImgSrc(undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestCameraSubjectChangeResponse]);
    // clean up freeze frame image when animation is over
    (0, react_1.useEffect)(() => {
        if (!animating && freezeFrameImgSrc) {
            setFreezeFrameImgSrc(undefined);
        }
        if (animating && freezeFrameImgSrc) {
            // the canvas image is flipped during the animated transition
            // this timeout removes the freeze frame image halfway through that animation, which is what we want for the animation
            setTimeout(() => {
                setTimeout(() => {
                    setFreezeFrameImgSrc(undefined);
                }, animationDuration / 2);
            }, animationDelay);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animating, animationDuration]);
    /** Multiplexer */
    const canvasContainer = (0, react_1.useRef)(null);
    const { width: canvasWidth, height: canvasHeight } = (_a = (0, use_multiplexer_1.useMultiplexer)(roomSession, videoController, canvasContainer)) !== null && _a !== void 0 ? _a : { width: 1280, height: 720 };
    // While faceToFaceModeEnabled determines if this is a 2WV call and if we should show audio controls on the video frame,
    // this is to place the camera flip button only on the remote video so local users cannot use this button to flip the camera on their video frame
    const canRequestCameraSubjectChange = !videoController.ownerIsLocalUser;
    const timerRef = (0, react_1.useRef)(null);
    const handleRequestCameraFlip = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        const requestedCameraSubject = (0, sdk_core_1.flipCamera)(cameraSubject);
        const requestId = roomSession.camera.requestCameraSubjectChange(requestedCameraSubject);
        setCameraSubjectChangeRequestId(requestId);
        setCameraRequestState(CameraRequestState.PENDING);
        // If we don't get a response from mobile after X seconds, we'll timeout the request by responding to it ourselves with an abort status
        timerRef.current = setTimeout(() => {
            var _a;
            // NOTE: needed to use ref to avoid stale closure on responses
            const response = (_a = responsesRef.current) === null || _a === void 0 ? void 0 : _a.find(r => r.inResponseToWallItemId === requestId);
            if (!response) {
                roomSession.camera.abortCameraSubjectChangeRequest(requestId);
                setCameraSubjectChangeRequestId(null);
                setCameraRequestState(CameraRequestState.REQUEST_TIMEOUT);
            }
        }, CAM_SUBJECT_REQUEST_TIMEOUT);
    };
    const resetToInitialCameraState = () => {
        setCameraRequestState(CameraRequestState.INITIAL);
    };
    const handleExitScreenshareRequest = () => {
        const requestId = roomSession.camera.requestCameraSubjectChange(latestRemoteCameraSubjectThatIsNotScreenshare === undefined
            ? streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_PERSON
            : latestRemoteCameraSubjectThatIsNotScreenshare);
        setCameraSubjectChangeRequestId(requestId);
        setCameraRequestState(CameraRequestState.PENDING);
    };
    const handleRequestScreenShare = (0, react_1.useCallback)(() => {
        const requestId = roomSession.camera.requestCameraSubjectChange(streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_SCREEN);
        setCameraSubjectChangeRequestId(requestId);
        setCameraRequestState(CameraRequestState.PENDING);
    }, [roomSession]);
    // Only show screenshare button for remote user who has screenshare capabilities
    const canRequestScreenshare = !videoController.ownerIsLocalUser &&
        ((_c = (_b = clientCapabilities[0]) === null || _b === void 0 ? void 0 : _b.canSendCameraSubjects) !== null && _c !== void 0 ? _c : []).includes(streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_SCREEN) &&
        screenshareEnabled;
    const Arrow = ({ flipArrow }) => ((0, jsx_runtime_1.jsx)(ArrowContainer, Object.assign({ flipArrow: flipArrow }, { children: (0, jsx_runtime_1.jsx)(ui_react_1.AppIcon, { name: "ArrowLongIcon", color: "white", style: {
                transform: flipArrow ? 'scaleX(-1)' : '',
            } }) })));
    const dataTestId = canRequestCameraSubjectChange
        ? 'video-frame-test-id-remote'
        : 'video-frame-test-id-local';
    const canvasAspectRatioConversion = (0, use_canvas_orientation_1.useCanvasOrientation)(canvasWidth, canvasHeight);
    return ((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: (0, jsx_runtime_1.jsxs)(VideoContainer, Object.assign({ styles: styles, theme: theme, "data-testid": dataTestId, ref: ref, animating: animating }, { children: [(0, jsx_runtime_1.jsxs)(CanvasWrapper, Object.assign({ "data-testid": "canvas-wrapper", "data-queryselector": "canvas-wrapper", ref: canvasContainer }, { children: [(0, jsx_runtime_1.jsx)(FreezeFrameImg, { src: freezeFrameImgSrc, alt: "video tranistion freeze frame", style: {
                                visibility: freezeFrameImgSrc !== undefined ? 'visible' : 'hidden',
                            } }), (0, jsx_runtime_1.jsx)(camera_inactive_screen_1.CameraInactiveScreen, { determinedUIProperties: determinedUIProperties, videoController: videoController, constrainContainerToCanvas: animating || constrainContainerToCanvas, aspectRatioConversion: canvasAspectRatioConversion })] })), (0, jsx_runtime_1.jsx)(loading_screen_1.LoadingScreen, { videoController: videoController }), canRequestCameraSubjectChange &&
                    (faceToFaceModeEnabled || canRequestScreenshare) && ((0, jsx_runtime_1.jsxs)(Constrainer, Object.assign({ constrainContainerToCanvas: constrainContainerToCanvas && !animating, canvasDims: { width: canvasWidth, height: canvasHeight }, animating: animating }, { children: [faceToFaceModeEnabled && ((0, jsx_runtime_1.jsx)(user_audio_status_1.UserAudioStatus, { videoController: videoController })), (0, jsx_runtime_1.jsxs)(ButtonContainer, { children: [faceToFaceModeEnabled &&
                                    cameraSubject !== streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_SCREEN && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(flip_camera_button_1.FlipCameraButton, { isCameraFlipRequest: (latestCameraSubjectChangeRequest === null || latestCameraSubjectChangeRequest === void 0 ? void 0 : latestCameraSubjectChangeRequest.requestedCameraSubject) ===
                                                streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_PERSON ||
                                                (latestCameraSubjectChangeRequest === null || latestCameraSubjectChangeRequest === void 0 ? void 0 : latestCameraSubjectChangeRequest.requestedCameraSubject) ===
                                                    streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_WORLD ||
                                                false, cameraSubject: cameraSubject, cameraRequestState: cameraRequestState, onRequest: () => {
                                                handleRequestCameraFlip();
                                            }, onCancel: resetToInitialCameraState }), showArrow && (0, jsx_runtime_1.jsx)(Arrow, { flipArrow: flipArrow })] })), canRequestScreenshare && ((0, jsx_runtime_1.jsx)(screenshare_button_1.ScreenshareButton, { isScreenShareRequest: (latestCameraSubjectChangeRequest === null || latestCameraSubjectChangeRequest === void 0 ? void 0 : latestCameraSubjectChangeRequest.requestedCameraSubject) ===
                                        streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_SCREEN || false, cameraSubject: cameraSubject, cameraRequestState: cameraRequestState, onEnterScreenshareRequest: () => {
                                        handleRequestScreenShare();
                                    }, onExitScreenshareRequest: handleExitScreenshareRequest, onCancel: resetToInitialCameraState }))] })] })))] })) }));
});
exports.VideoFrame.displayName = 'VideoFrame';
const FreezeFrameImg = ui_react_1.styled.img `
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    object-fit: contain;
    background: inherit;
    border-radius: 20px;
`;
const CanvasWrapper = ui_react_1.styled.div(() => {
    return {
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        borderRadius: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // Needed for rounded corners to work with desktop Safari.
        backfaceVisibility: 'hidden',
        transform: 'translate3d(0,0,0)',
        zIndex: 1,
    };
});
const ButtonContainer = ui_react_1.styled.div `
    position: absolute;
    top: 10px;
    right: 20px;
    z-index: 12;
    display: flex;
    flex-direction: column;
`;
const Constrainer = ({ animating, canvasDims, constrainContainerToCanvas, children, }) => {
    const { width: canvasWidth, height: canvasHeight } = canvasDims;
    const constrainerRef = (0, react_1.useRef)(null);
    const [parentDims, setParentDims] = (0, react_1.useState)({
        width: 0,
        height: 0,
    });
    // Adds a resize listener to the parent container.
    // When the parent changes size, the updated dimensions are updated in state
    // We only pay attention to the parent dims when 'constrainContainerToCanvas' is true
    // Otherwise, we let the constrainer occupy 100% width/height of the parent container
    (0, react_1.useLayoutEffect)(() => {
        const parentResizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const resizedParentDims = {
                    width: entry.contentRect.width,
                    height: entry.contentRect.height,
                };
                setParentDims(resizedParentDims);
            }
        });
        if (constrainerRef &&
            constrainerRef.current &&
            constrainerRef.current.parentElement &&
            constrainContainerToCanvas) {
            parentResizeObserver.observe(constrainerRef.current.parentElement);
        }
        if (constrainerRef &&
            constrainerRef.current &&
            constrainerRef.current.parentElement &&
            !constrainContainerToCanvas) {
            parentResizeObserver.unobserve(constrainerRef.current.parentElement);
        }
        return () => parentResizeObserver.disconnect();
    }, [constrainContainerToCanvas]);
    (0, react_1.useLayoutEffect)(() => {
        const constraintDims = (0, get_aspect_ratio_constraint_dims_1.getAspectRatioConstraintDims)(canvasWidth, canvasHeight, parentDims.width, parentDims.height);
        if (constrainerRef && constrainerRef.current && constrainContainerToCanvas) {
            // we only need to constrain the width, since we want the UI indicators anchored to the top and bottom of the parent container
            constrainerRef.current.style.setProperty('--aspect-ratio-width', `${constraintDims.width}px`);
        }
        if (constrainerRef && constrainerRef.current && !constrainContainerToCanvas) {
            // remove custom width property to enable 'width: 100%' fallback value
            constrainerRef.current.style.removeProperty('--aspect-ratio-width');
        }
        // eslint-disable-next-line
    }, [parentDims, canvasDims, constrainContainerToCanvas]);
    return ((0, jsx_runtime_1.jsx)("div", Object.assign({ style: {
            transition: 'opacity 100ms ease-out',
            opacity: animating ? 0 : 1,
            width: 'var(--aspect-ratio-width, 100%)',
            height: '100%',
            position: 'absolute',
        }, ref: constrainerRef }, { children: children })));
};
const VideoContainer = ui_react_1.styled.div(props => {
    // These are default styles for stacked view. Prop styles will override when in face to world.
    return Object.assign({ width: 'var(--item-width, 100%)', height: `var(--item-height, 100%)`, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', borderRadius: '20px', backgroundColor: props.theme.colors.grey90 }, props.styles);
});
const ArrowContainer = ui_react_1.styled.span(({ flipArrow }) => {
    return {
        position: 'absolute',
        right: flipArrow ? '48px' : '-28px',
        marginTop: '12px',
        top: '0',
        zIndex: 3,
    };
});
//# sourceMappingURL=video_frame.js.map